import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as CredentialsApi from 'api/Credentials';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';

const displayMessage = ({ type, msg }) => {
  dispatch(
    openSnackbar({
      open: true,
      message: msg ? msg : type === 'success' ? 'Success' : 'Something went wrong',
      variant: 'alert',
      alert: {
        color: type || 'success'
      },
      close: false
    })
  );
};

const initialState = {
  livekit: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    livekitData: null,
    ui: {
      loading: false,
      msg: ''
    }
  },
  storage: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    storageData: null,
    ui: {
      loading: false,
      msg: ''
    }
  },
  tenantData: null,
  ui: {
    loading: false,
    msg: ''
  }
};

const getLiveKitData = createAsyncThunk('credentials/getLiveKitData', async ({ params, signal }) => {
  const res = await CredentialsApi.getLivekitData(params, signal);
  return res?.data?.data;
});

const createLivekit = createAsyncThunk('credentials/createLivekit', async (params) => {
  try {
    const res = await CredentialsApi.createLivekit(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateLivekit = createAsyncThunk('credentials/updateLivekit', async (params) => {
  try {
    const res = await CredentialsApi.updateLivekit(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteLivekit = createAsyncThunk('credentials/deleteLivekit', async (id) => {
  try {
    const res = await CredentialsApi.deleteLivekit(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

//storage thunks
const getStorageData = createAsyncThunk('credentials/getStorageData', async ({ params, signal }) => {
  const res = await CredentialsApi.getStorageData(params, signal);
  return res?.data?.data;
});

const createStorage = createAsyncThunk('credentials/createStorage', async (params) => {
  try {
    const res = await CredentialsApi.createStorage(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateStorage = createAsyncThunk('credentials/updateStorage', async (params) => {
  try {
    const res = await CredentialsApi.updateStorage(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteStorage = createAsyncThunk('credentials/deleteStorage', async (id) => {
  try {
    const res = await CredentialsApi.deleteStorage(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getAllTenants = createAsyncThunk('credentials/getAllTenants', async () => {
  try {
    const res = await CredentialsApi.getAllCusotmers();
    // displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const credentialsSlice = createSlice({
  name: 'credentials',
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setLivekitCurrentPage: (state, action) => {
      state.livekit.pagination.currentPage = action.payload;
    },
    setLivekitPageSize: (state, action) => {
      state.livekit.pagination.pageSize = action.payload;
    },
    setStorageCurrentPage: (state, action) => {
      state.storage.pagination.currentPage = action.payload;
    },
    setStoragePageSize: (state, action) => {
      state.storage.pagination.pageSize = action.payload;
    }
  },
  extraReducers: {
    [getLiveKitData.pending]: (state) => {
      state.livekit.ui.loading = true;
    },
    [getLiveKitData.fulfilled]: (state, action) => {
      state.livekit.ui.loading = false;
      state.livekit.pagination.totalRecords = action.payload?.count;
      state.livekit.livekitData = action.payload?.results || [];
    },
    [getLiveKitData.rejected]: (state, action) => {
      state.status = 'error';
      state.livekit.ui.loading = false;
      state.error = action.error.message;
    },

    [createLivekit.pending]: (state) => {
      state.livekit.ui.loading = true;
    },
    [createLivekit.fulfilled]: (state) => {
      state.livekit.ui.loading = false;
    },
    [createLivekit.rejected]: (state, action) => {
      state.status = 'error';
      state.livekit.ui.loading = false;
      state.error = action.error.message;
    },

    [updateLivekit.pending]: (state) => {
      state.livekit.ui.loading = true;
    },
    [updateLivekit.fulfilled]: (state) => {
      state.livekit.ui.loading = false;
    },
    [updateLivekit.rejected]: (state, action) => {
      state.status = 'error';
      state.livekit.ui.loading = false;
      state.error = action.error.message;
    },

    [deleteLivekit.pending]: (state) => {
      state.livekit.ui.loading = true;
    },
    [deleteLivekit.fulfilled]: (state) => {
      state.livekit.ui.loading = false;
    },
    [deleteLivekit.rejected]: (state, action) => {
      state.status = 'error';
      state.livekit.ui.loading = false;
      state.error = action.error.message;
    },

    //storage reducer
    [getStorageData.pending]: (state) => {
      state.storage.ui.loading = true;
    },
    [getStorageData.fulfilled]: (state, action) => {
      state.storage.ui.loading = false;
      state.storage.pagination.totalRecords = action.payload?.count;
      state.storage.storageData = action.payload?.results || [];
    },
    [getStorageData.rejected]: (state, action) => {
      state.status = 'error';
      state.storage.ui.loading = false;
      state.error = action.error.message;
    },

    [createStorage.pending]: (state) => {
      state.storage.ui.loading = true;
    },
    [createStorage.fulfilled]: (state) => {
      state.storage.ui.loading = false;
    },
    [createStorage.rejected]: (state, action) => {
      state.status = 'error';
      state.storage.ui.loading = false;
      state.error = action.error.message;
    },

    [updateStorage.pending]: (state) => {
      state.storage.ui.loading = true;
    },
    [updateStorage.fulfilled]: (state) => {
      state.storage.ui.loading = false;
    },
    [updateStorage.rejected]: (state, action) => {
      state.status = 'error';
      state.storage.ui.loading = false;
      state.error = action.error.message;
    },

    [deleteStorage.pending]: (state) => {
      state.storage.ui.loading = true;
    },
    [deleteStorage.fulfilled]: (state) => {
      state.storage.ui.loading = false;
    },
    [deleteStorage.rejected]: (state, action) => {
      state.status = 'error';
      state.storage.ui.loading = false;
      state.error = action.error.message;
    },
    //tenant reducer
    [getAllTenants.pending]: (state) => {
      state.ui.loading = true;
    },
    [getAllTenants.fulfilled]: (state, action) => {
      state.ui.loading = false;
      state.tenantData = action.payload;
    },
    [getAllTenants.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    }
  }
});

const { resetState, setLivekitCurrentPage, setLivekitPageSize, setStorageCurrentPage, setStoragePageSize } = credentialsSlice.actions;

export default credentialsSlice.reducer;

export {
  resetState,
  setLivekitCurrentPage,
  setLivekitPageSize,
  getLiveKitData,
  createLivekit,
  updateLivekit,
  deleteLivekit,
  getAllTenants,
  getStorageData,
  createStorage,
  updateStorage,
  deleteStorage,
  setStorageCurrentPage,
  setStoragePageSize
};
